import { ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormGroupDirective, NgForm, ReactiveFormsModule, Validators } from '@angular/forms';
import { MaterialModule } from '../../../../../shared/modules/material/material.module';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatSelect } from '@angular/material/select';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { CommonValidators } from '../../../../../core/validators';
import { ErrorStateMatcher } from '@angular/material/core';
import { CapitalizePipe } from '../../../../../core/pipes/capitalize.pipe';
import { ActionContext, PronounTypes, SelectionItem, UserDetail, UserUpdate } from '../../../../../core/models/user.models';
import { UserService } from '../../../../../core/services/user/user.service';
import { FeeService } from '../../../../../core/services/fee/fee.service';
import { Specialty } from '../../../../../core/models/specialties.models';
import { LangService } from '../../../../../core/services/lang/lang.service';
import { Lang, LanguageTypes } from '../../../../../core/models/lang.models';

export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@Component({
  selector: 'app-profile-data-submenu',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule, FontAwesomeModule, TranslateModule, CapitalizePipe],
  templateUrl: './profile-data-submenu.component.html',
  styleUrl: './profile-data-submenu.component.scss'
})
export class ProfileDataSubmenuComponent {
  @ViewChild("pronounSelected", { read: MatSelect, static: false }) pronounSelect!: MatSelect;
  @ViewChild("specialtySelected", { read: MatSelect, static: false }) specialtySelect!: MatSelect;
  @ViewChild("languageSelected", { read: MatSelect, static: false }) languageSelect!: MatSelect;
  @ViewChild("categorySelected", { read: MatSelect, static: false }) categorySelect!: MatSelect;

  @Input() mode: "create" | "update" = "create";
  @Input() user!: UserDetail;
  @Input() context!: ActionContext;

  matcher = new MyErrorStateMatcher();
  actionContextEnum = ActionContext;
  inputTypePassword = 'password';
  inputTypeRepeatPassword = 'password';
  specialties: Specialty[] = [];

  pronounsTypes: SelectionItem[] = [];
  languagesTypes: Lang[] = [];
  categories: string[] = [];

  //To prevent the autocomplete from appearing in the inputs remove 
  //the Validators.required, and handle this value manually
  formGroup!: FormGroup;

  constructor(private translateService: TranslateService, private feeService: FeeService, private langService: LangService,
    private userService: UserService, private cdr: ChangeDetectorRef) {
    this.formGroup = new FormGroup(
      {
        username: new FormControl<string>("", [Validators.required]),
        password: new FormControl<string>("", [Validators.required, Validators.pattern(new RegExp('(?=.*?[A-Z])')), Validators.minLength(8)]),
        repeatPassword: new FormControl<string>("", [Validators.required]),
        pronounType: new FormControl<string[]>([]),
        firstName: new FormControl<string>("", [Validators.required, Validators.min(2), Validators.max(50)]),
        lastName: new FormControl<string>("", [Validators.required, Validators.min(2), Validators.max(50)]),
        specialityId: new FormControl<string[]>([]),
        category: new FormControl<string[]>([]),
        langId: new FormControl<string[]>([]),
        externalId: new FormControl<string>(""),
        admin: new FormControl<boolean>(false),


        invisibleControlNumbers: new FormControl('', [(Validators.pattern(new RegExp('(?=.*?[0-9])')))]),
        invisibleControlLowercase: new FormControl('', [(Validators.pattern(new RegExp('(?=.*?[a-z])')))]),
        invisibleControlNonAlphanumeric: new FormControl('', [(Validators.pattern(new RegExp('[^a-zA-Z0-9]')))])
      },
      {
        validators: CommonValidators.confirmedValidator('password', 'repeatPassword')

      }
    )

    this.pronounsTypes = [
      {
        id: PronounTypes.MISTER,
        description: this.translateService.instant("configuration-page.action-dialog.pronoun-mr")
      },
      {
        id: PronounTypes.MISS,
        description: this.translateService.instant("configuration-page.action-dialog.pronoun-ms")
      },
    ]

  }

  ngOnInit() {
    this.feeService.getSpecialties().subscribe((specialties: Specialty[]) => {
      this.specialties = specialties;
    })
    this.langService.getAll().subscribe((langs: Lang[]) => {
      this.languagesTypes = langs;
    })
    this.userService.getAllCategories().subscribe((categories: string[]) => {
      this.categories = categories;
    })
  }

  ngAfterViewInit() {
    if (this.user) {
      this.formGroup.get("username")?.disabled;
      const { userName, firstName, lastName, admin, pronounType, specialityId, category,
        externalId, langId } = this.user;

      this.formGroup.get("username")?.setValue(userName);
      this.formGroup.get("firstName")?.setValue(firstName);
      this.formGroup.get("lastName")?.setValue(lastName);
      this.formGroup.get("admin")?.setValue(admin);
      this.formGroup.get("pronounType")?.setValue([pronounType]);
      this.formGroup.get("specialityId")?.setValue([specialityId?.toString()]);
      this.formGroup.get("category")?.setValue([category]);
      this.formGroup.get("externalId")?.setValue(externalId);
      this.formGroup.get("langId")?.setValue([langId]);

      this.formGroup.get("username")?.removeValidators(Validators.required);
      this.formGroup.get("password")?.removeValidators(Validators.required);
      this.formGroup.get("repeatPassword")?.removeValidators(Validators.required);
      this.formGroup?.get("username")?.updateValueAndValidity();
      this.formGroup?.get("password")?.updateValueAndValidity();
      this.formGroup?.get("repeatPassword")?.updateValueAndValidity();

      this.cdr.detectChanges();
    }

    this.formGroup.get('password')!.valueChanges.subscribe((value: string) => {
      this.formGroup.get('invisibleControlNumbers')!.setValue(value);
      if (this.formGroup.get('invisibleControlNumbers')!.hasError('pattern')) {
        this.formGroup.get('password')?.setErrors({ 'numeric': true });
        this.formGroup.updateValueAndValidity()
      }

      this.formGroup.get('invisibleControlLowercase')!.setValue(value);
      if (this.formGroup.get('invisibleControlLowercase')!.hasError('pattern')) {
        this.formGroup.get('password')?.setErrors({ 'lowercase': true });
        this.formGroup.updateValueAndValidity()
      }

      this.formGroup.get('invisibleControlNonAlphanumeric')!.setValue(value);
      if (this.formGroup.get('invisibleControlNonAlphanumeric')!.hasError('pattern')) {
        this.formGroup.get('password')?.setErrors({ 'alphaNumeric': true });
        this.formGroup.updateValueAndValidity()
      }
    })
  }

  clearInput(key: string) {
    this.formGroup.get(key)?.reset();
  }

  updateChkbxArray(isChecked: boolean, id: string, key: string) {
    const formControl = this.formGroup.get(key);
    isChecked ? formControl?.setValue([id]) : formControl?.setValue([]);
  }

  //This function is created because otherwise when you click on the mat-option text (not the mat-checkbox) 
  //the updateChkbxArray functionality is not triggered.
  propagateCheck(recordId: string, formControlName: string) {
    const currentState = this.isChecked(recordId, formControlName);
    this.updateChkbxArray(currentState, recordId, formControlName);
  }

  isChecked(recordId: string, controlName: string): boolean {
    const formControl = this.formGroup.get(controlName)?.value;
    return formControl.includes(recordId);
  }

  toogleInputPassword(inputId: string, type: string) {
    if (inputId === "inputPassword") {
      this.inputTypePassword = type;
    } else if (inputId === "inputRepeatPassword") {
      this.inputTypeRepeatPassword = type;
    }
  }

}
